import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { GoodsItemType } from "../../../../types/goods";

import { useUserContext } from "../../../../contexts/userContext";

import {
  getGoodsRentalListDetail,
  requestGoodsRental,
} from "../../../../services/goodsRentalServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";

import ServiceRentDetailHeader from "../../../../components/Service/Rent/DetailHeader";
import BottomButtonGroup from "../../../../components/Button/Bottom";
import StickyHeader from "../../../../components/Layout/StickyHeader";

type Props = {};

export default function ServiceRentalDetail(props: Props) {
  const params = useParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [goodsItem, setGoodsItem] = useState<GoodsItemType>();
  const [serviceList, setServiceList] = useState<
    ServiceRentalDetailServiceItemProps[]
  >([]);
  const navigate = useNavigate();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [user] = useUserContext();

  const contentDisplayBuildingIdList = ["TG_0007100", "TG_0007126"]; // 한진빌딩 본관/신관

  const getPolicyContent = (goodsTypeName?: string) => {
    const content = `1회 ${
      goodsTypeName === "책" ? "14일" : "2일(48시간)"
    }\n1인 1개 대여 가능`;

    if (
      goodsTypeName === "우산" &&
      user.buildingId &&
      contentDisplayBuildingIdList.includes(user.buildingId)
    ) {
      const addContent = `\n96시간 반납 지연 시 분실로 간주\n분실, 손상 시 10,000원(2023.1.1일부) 부과\n24:00이후 ▶ 본관 경비실에서 대여가능`;
      return `${content}${addContent}`;
    }
    return content;
  };

  const loadRentalDetail = async (id: string, canceled?: boolean) => {
    setIsLoading(true);
    try {
      const result = await getGoodsRentalListDetail(id);
      if (result.status === HttpStatusCode.Ok) {
        const data = canceled
          ? { ...result.data, activeRentalHistory: undefined }
          : result.data;
        setGoodsItem(data);
        setServiceList([
          {
            title: "대여/반납 장소",
            content: `${result.data.buildingName}\n${result.data.floorName} ${result.data.storeName}`,
          },
          {
            title: "대여 규정",
            content: getPolicyContent(result.data.goodsTypeName),
          },
        ]);
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("렌트 정보 조회에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRentStock = async () => {
    if (!goodsItem?.stockId) return;

    const onClick = () => {
      if (params?.id) {
        loadRentalDetail(params.id);
      }
    };

    setIsLoading(true);
    try {
      const result = await requestGoodsRental(goodsItem?.stockId);
      if (result.status === HttpStatusCode.Ok) {
        navigate("/service/rental/requested", {
          state: {
            ...result.data,
            buildingId: goodsItem?.buildingId,
            storeId: goodsItem?.storeId,
            stockId: goodsItem?.stockId,
          },
        });
      } else {
        open("물품 대여 예약에 실패했습니다.", { onClick });
      }
    } catch (e: any) {
      const message = e?.response?.data?.error
        ? e.response.data.error?.replace(". ", ".\n")
        : "물품 대여 예약에 실패했습니다.";
      open(message, { onClick });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    if (params?.id) {
      loadRentalDetail(params.id);
    }
  }, [params.id, user.isLoading]);

  useEffect(() => {
    const handleScroll = () => {
      if ((window?.scrollY ?? 0) > 220) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window?.addEventListener("scroll", handleScroll);

    // Clean up function
    return () => {
      window?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="flex flex-col">
      {loadingComponent}
      <StickyHeader
        title="대여하기"
        onBackClick={() =>
          navigate(location?.state?.parentUrl ?? "/service/rental", {
            state: location?.state?.parentState,
          })
        }
        className={clsx([
          "px-5 transition-all !fixed w-full",
          {
            "bg-white text-black": isSticky,
            "!bg-transparent text-transparent": !isSticky,
          },
        ])}
        backIconColor={goodsItem?.activeRentalHistory ? "white" : "black"}
      />

      <ServiceRentDetailHeader
        url={
          goodsItem?.goodsImg?.items.find((item) => item.scale === "medium")
            ?.url
        }
        activityCount={goodsItem?.activityCount ?? 0}
        status={goodsItem?.activeRentalHistory?.status}
        goodsServiceId={goodsItem?.activeRentalHistory?.goodsServiceId}
        onLoad={() => {
          if (params?.id) {
            loadRentalDetail(params.id, true);
          }
        }}
        storeId={goodsItem?.storeId}
        createdAt={goodsItem?.activeRentalHistory?.createdAt}
        rentalAt={goodsItem?.activeRentalHistory?.returnDueAt}
        stockId={goodsItem?.stockId}
        // TODO: 타입 체크도 할지 고려, 현재 한글로만 내려줘서 귀찮은 상태
        // type={goodsItem?.goodsTypeName}
        buildingInfo={{
          buildingId: goodsItem?.buildingId,
          buildingFloorId: goodsItem?.buildingFloorId,
          buildingName: goodsItem?.buildingName,
          buildingFloorName: goodsItem?.floorName,
        }}
        returnStoreList={goodsItem?.returnStoreList}
      />
      <div className="p-5 pt-[30px] flex flex-col gap-2.5 items-start self-stretch">
        <div className="flex py-1 pl-1.5 pr-2 items-center gap-1.5 rounded bg-brand-primary-gray-100 text-white text-xs leading-4">
          <div className="h-1 w-1 bg-white rounded-full"></div>
          {goodsItem?.buildingName}
        </div>
        <div className=" text-2xl font-semibold leading-[29px]">
          {goodsItem?.goodsName}
        </div>
        <div className=" text-brand-neutral-40 leading-[19px]">
          {goodsItem?.goodsDescription}
        </div>
      </div>

      <div className="p-5 pt-[30px] flex flex-col items-start gap-2.5">
        <div className=" text-lg font-medium leading-[21px]">서비스 정보</div>
        {goodsItem?.useStock && (
          <div className="flex py-[15px] px-5 items-start self-stretch bg-brand-neutral-05 rounded-[3px] flex-col">
            <div className="text-xs leading-4 text-brand-neutral-70">
              잔여 개수
            </div>
            <div className="text-base font-normal leading-[26px]">
              {goodsItem?.activityCount ?? 0}{" "}
              <span className="text-brand-neutral-50">
                / {goodsItem?.totalCount ?? 0}
              </span>
            </div>
          </div>
        )}
        <div className="flex flex-col items-start self-stretch border-t border-black">
          {serviceList.map((service, index) => (
            <ServiceRentalDetailServiceItem
              key={index}
              {...service}
              index={index}
            />
          ))}
        </div>
      </div>

      {!goodsItem?.activeRentalHistory && (
        <BottomButtonGroup
          title={
            (goodsItem?.activityCount ?? 0) <= 0
              ? "대여 가능한 수량이 없어요"
              : "대여 신청하기"
          }
          onClick={handleRentStock}
          disabled={(goodsItem?.activityCount ?? 0) <= 0}
        />
      )}
      {simplePopupComponent}
    </div>
  );
}

export type ServiceRentalDetailServiceItemProps = {
  title?: string;
  content?: string;
  index?: number;
};

export const ServiceRentalDetailServiceItem = ({
  title,
  content,
  index,
}: ServiceRentalDetailServiceItemProps) => {
  return (
    <div
      className={clsx([
        "flex py-1.5 items-start gap-2.5 self-stretch",
        { "border-t border-t-brand-neutral-10": (index ?? 0) > 0 },
      ])}
    >
      <div className="flex w-[90px] py-3 flex-col items-start gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
        {title}
      </div>
      <div className="flex p-2.5 flex-col items-start gap-[5px] flex-[1_0_0] text-base leading-[26px] self-stretch whitespace-pre-line">
        {content}
      </div>
    </div>
  );
};
